import { Link, usePage } from "@inertiajs/react"
import { Trans, useLingui } from "@lingui/react/macro"
import { useState } from "react"
import type {
  ContactDrawer as ContactDrawerData,
  DrawerActivity,
} from "@/types/v2/conversation"
import { ActivityFeed } from "@/components/v2/deals/activity-feed"
import {
  ContactActivityScopeRow,
  filterContactActivityByScope,
} from "@/components/v2/contacts/contact-activity-scope-row"
import {
  BASE_RELOAD_PROPS,
  setContactActivityScope,
  type DrawerNavContext,
} from "@/lib/drawer-trail"
import { DrawerNavContextValue } from "@/components/v2/drawer/drawer-nav-context"
import {
  currentActivitySearch,
  runActivitySearch,
} from "@/lib/activity-search"
import { ContactInlineEditRow } from "@/components/v2/contacts/contact-inline-edit-row"
import { ContactAssigneesRow } from "@/components/v2/contacts/contact-assignees-row"
import type { ContactPropKey } from "@/lib/contact-prop-update"
import { useStartCall } from "@/hooks/use-start-call"

interface ContactEntryBodyProps {
  contact: ContactDrawerData
  // Drawer navigation context for the activity scope row
  // (it rewrites the URL when the user picks a scope pill).
  navContext: DrawerNavContext
  // Eager activity payload supplied by the command-palette
  // rich-preview pane (CON-183). The URL-driven slideover
  // leaves this undefined and reads the deferred
  // `drawerActivity` Inertia prop instead; the palette has no
  // such deferred prop, so it fetches the activity alongside
  // the contact and hands it in here.
  activity?: DrawerActivity
}

// Body content for a contact entry in the unified v2
// slideover. Extracted from the legacy `ContactDrawer` so
// the chrome (DrawerShell + DrawerHeader) lives in one
// place — the unified Drawer — and the entry components
// are just their content.
// The contact entry stays READ-ONLY by product design —
// notes in v2 are deal-anchored, so the contact slideover
// surfaces the activity timeline but does NOT grow the
// composer affordances. Single-scroll body: identity +
// activity feed flow naturally; the activity feed's chip
// switches keep this body's scrollTop in place (single
// shared scroll memory).
export function ContactEntryBody({
  contact,
  navContext,
  activity,
}: ContactEntryBodyProps) {
  const previewMode = Boolean(navContext.preview)
  const propKey = previewMode ? "preview" : "drawer"

  const [scrollContainer, setScrollContainer] =
    useState<HTMLElement | null>(null)

  // The timeline items + scope pills arrive via the deferred
  // `drawer_activity` prop (the eager `activityCount` rides
  // in the drawer payload). While that follow-up request is
  // in flight, the feed shimmers a count-sized placeholder —
  // identity/properties above stay rendered. A count of 0
  // means no activity, so we skip the shimmer and let the
  // feed show its empty state directly.
  //
  // In preview mode the palette passes `activity` directly
  // (no Inertia deferred prop exists there), so we prefer it
  // and never shimmer waiting on a prop that won't arrive.
  const { props } = usePage<{ drawerActivity?: DrawerActivity }>()
  const drawerActivity = activity ?? props.drawerActivity
  const loading =
    drawerActivity === undefined && contact.activityCount > 0
  const scopes = drawerActivity?.activityScopes ?? []
  const active = drawerActivity?.activeActivityScope
  const items =
    drawerActivity && active
      ? filterContactActivityByScope(drawerActivity.activity, active)
      : []

  return (
    <DrawerNavContextValue.Provider value={navContext}>
    <div
      ref={setScrollContainer}
      data-testid="contact-drawer-body"
      className="flex-1 min-h-0 overflow-y-auto px-6 pt-6 pb-8 flex flex-col gap-6"
    >
      <Hero contact={contact} />
      <Actions contact={contact} />
      <PropertiesCard contact={contact} contactPropKey={propKey} />
      {!loading && active && scopes.length > 1 ? (
        <ContactActivityScopeRow
          scopes={scopes}
          active={active}
          testId="contact-drawer-scope-row"
          onSelect={
            previewMode
              ? (scope) => navContext.preview!.setScope(scope)
              : (scope) =>
                  setContactActivityScope(contact.id, scope, navContext)
          }
        />
      ) : null}
      <div
        data-testid="contact-drawer-activity"
        className="flex flex-col gap-3 pt-3 border-t border-[#F0EDE6]"
      >
        <SectionLabel><Trans>Activity</Trans></SectionLabel>
        <ActivityFeed
          items={items}
          variant="drawer"
          scrollContainer={scrollContainer}
          loading={loading}
          loadingRowCount={contact.activityCount}
          search={{
            initialValue: currentActivitySearch(),
            onSearch: previewMode
              ? (value) => navContext.preview!.searchActivity(value)
              : (value) =>
                  runActivitySearch(
                    navContext.path,
                    [
                      ...BASE_RELOAD_PROPS,
                      ...(navContext.reloadProps ?? []),
                    ],
                    value,
                  ),
          }}
        />
      </div>
    </div>
    </DrawerNavContextValue.Provider>
  )
}

function Hero({ contact }: { contact: ContactDrawerData }) {
  const subtitle = buildSubtitle(contact)
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex items-center justify-center shrink-0 rounded-full bg-[#E5DFD3] size-11">
        <div className="font-bold text-[#1E1B17] text-base leading-[18px]">
          {contact.initials}
        </div>
      </div>
      <div className="grow basis-0 min-w-0 flex flex-col gap-0.5">
        {subtitle ? (
          <div className="text-[#7A746B] text-[13px] leading-4 truncate">
            {subtitle}
          </div>
        ) : null}
        <div className="tracking-[-0.01em] font-semibold text-[#1E1B17] text-[22px] leading-7 truncate">
          {contact.name}
        </div>
      </div>
    </div>
  )
}

function buildSubtitle(contact: ContactDrawerData): string | null {
  const parts: string[] = []
  if (contact.company?.name) parts.push(contact.company.name)
  if (contact.jobTitle) parts.push(contact.jobTitle)
  if (parts.length === 0) return null
  return parts.join(" · ")
}

// "Open chat" always works:
// - Existing conversation → land on it in the inbox.
// - No conversation yet → land on the inbox with
//   `?compose_contact_id=N`, which the inbox controller
//   serves a `composeSeedContact` prop for; the inbox
//   page auto-opens the compose modal pre-filled with
//   that contact.
function Actions({ contact }: { contact: ContactDrawerData }) {
  const href =
    contact.primaryConversationId != null
      ? `/v2/inbox?conversation_id=${contact.primaryConversationId}`
      : `/v2/inbox?compose_contact_id=${contact.id}`

  // Outbound voice is server-driven via the shared
  // `useStartCall` hook — identical gating + POST to the
  // contacts-index row and the deal contact card. The hook
  // reads `voiceConfig` off the Inertia page, so no prop
  // wiring is needed here.
  const { t } = useLingui()
  const { startCall, busy, disabled, disabledReason } = useStartCall({
    contactId: contact.id,
    phone: contact.phone,
  })

  return (
    <div className="flex gap-2">
      <Link
        href={href}
        data-testid="contact-drawer-open-chat"
        className="grow basis-0 flex items-center justify-center rounded-lg py-2.5 px-3.5 gap-2 bg-[#1E1B17] text-[#FAF7F1] text-sm font-semibold leading-[18px] cursor-pointer hover:bg-[#2A2620]"
      >
        <WhatsAppIcon />
        <Trans>Open chat</Trans>
      </Link>
      <button
        type="button"
        onClick={disabled || busy ? undefined : startCall}
        disabled={disabled || busy}
        data-testid="contact-drawer-call"
        aria-label={disabledReason ? t`Call (${disabledReason})` : t`Call`}
        title={disabledReason ?? undefined}
        className="grow basis-0 flex items-center justify-center rounded-lg py-2.5 px-3.5 gap-2 bg-card border border-[#DDD6C7] text-[#1E1B17] text-sm font-semibold leading-[18px] enabled:cursor-pointer enabled:hover:bg-[#FAF6EE] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <PhoneIcon />
        {busy ? <Trans>Calling…</Trans> : <Trans>Call</Trans>}
      </button>
    </div>
  )
}

function PropertiesCard({
  contact,
  contactPropKey,
}: {
  contact: ContactDrawerData
  contactPropKey: ContactPropKey
}) {
  // Same set + ordering as the desktop / mobile show
  // pages so the contact experience feels identical
  // across all three surfaces. Owner and Source remain
  // static rows; the other four fields are inline-
  // editable via `ContactInlineEditRow`.
  const { t } = useLingui()
  return (
    <section className="flex flex-col gap-2.5">
      <SectionLabel><Trans>Properties</Trans></SectionLabel>
      <div className="flex flex-col rounded-xl overflow-hidden bg-white border border-[#E5DFD3]">
        <ContactInlineEditRow
          contactId={contact.id}
          contactPropKey={contactPropKey}
          label={t`Email`}
          field="email"
          value={contact.email}
          inputType="email"
          inputMode="email"
          placeholder={t`name@example.com`}
        />
        <Divider />
        <ContactInlineEditRow
          contactId={contact.id}
          contactPropKey={contactPropKey}
          label={t`Phone`}
          field="phone"
          value={contact.phone}
          readOnly={!contact.phoneEditable}
          inputType="tel"
          inputMode="tel"
          placeholder={t`+1 555 0123`}
        />
        <Divider />
        <ContactInlineEditRow
          contactId={contact.id}
          contactPropKey={contactPropKey}
          label={t`LinkedIn`}
          field="linkedin_url"
          value={contact.linkedinUrl}
          inputType="url"
          inputMode="url"
          placeholder={t`https://linkedin.com/in/…`}
          readDisplay={
            contact.linkedinUrl && contact.linkedinDisplay ? (
              <PlainValue text={contact.linkedinDisplay} />
            ) : undefined
          }
        />
        <Divider />
        <ContactAssigneesRow
          contactId={contact.id}
          contactName={contact.name}
          assignees={contact.assignees}
          contactPropKey={contactPropKey}
        />
        <Divider />
        <StaticRow label={t`Source`}>
          <div className="flex items-center gap-1.5 min-w-0">
            {contact.source.kind === "whatsapp" ? <WhatsAppDot /> : null}
            <span className="font-medium text-[#1E1B17] text-[13px] leading-4 truncate min-w-0">
              {contact.source.label}
            </span>
          </div>
        </StaticRow>
        <Divider />
        <ContactInlineEditRow
          contactId={contact.id}
          contactPropKey={contactPropKey}
          label={t`Location`}
          field="city"
          // Edits write back to `city`; the read display
          // prefers `city` and falls back to the derived
          // `location` (city || address) when blank, with
          // the UTC offset suffixed in either case so we
          // don't drop the timezone signal the drawer
          // used to surface here.
          value={contact.city}
          readDisplay={renderLocationReadDisplay(contact)}
        />
      </div>
    </section>
  )
}

function StaticRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center py-3 px-4 gap-3">
      <span className="grow-0 shrink-0 text-[#7A746B] text-[13px] leading-4">
        {label}
      </span>
      <div className="grow basis-0 min-w-0 flex justify-end items-center gap-1.5">
        {children}
      </div>
    </div>
  )
}

function Divider() {
  return <div className="h-px ml-4 bg-[#F0EDE6]" />
}

function renderLocationReadDisplay(
  contact: ContactDrawerData,
): React.ReactNode | undefined {
  const display = contact.city ?? contact.location ?? null
  const tz = contact.timeZoneLabel ?? null
  if (!display && !tz) return undefined
  const combined = [display, tz].filter(Boolean).join(" · ")
  return (
    <span className="text-[13px] leading-4 font-medium text-foreground truncate min-w-0">
      {combined}
    </span>
  )
}

function PlainValue({ text }: { text: string }) {
  return (
    <span className="text-[13px] leading-4 font-medium text-foreground truncate min-w-0">
      {text}
    </span>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="tracking-[0.06em] uppercase font-semibold text-[#7A746B] text-[11px] leading-3">
      {children}
    </div>
  )
}

function WhatsAppIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M27.012 4.876C24.084 1.94 20.189.323 16.04.321 7.486.321.523 7.281.52 15.838c0 2.735.715 5.405 2.072 7.76L.389 31.679l8.27-2.169a15.55 15.55 0 0 0 7.374 1.878h.007c8.554 0 15.518-6.961 15.521-15.519.001-4.148-1.612-8.046-4.549-10.992m-10.972 23.88h-.005a12.9 12.9 0 0 1-6.572-1.799l-.471-.279-4.907 1.287 1.31-4.784-.307-.49a12.9 12.9 0 0 1-1.977-6.881c.003-7.114 5.792-12.901 12.937-12.901a12.85 12.85 0 0 1 9.139 3.79 12.85 12.85 0 0 1 3.781 9.146c-.003 7.115-5.792 12.91-12.928 12.91"
        fill="#FFFFFF"
      />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#1E1B17"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  )
}

function WhatsAppDot() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M8 2C4.69 2 2 4.69 2 8C2 9.16 2.34 10.24 2.91 11.16L2 14L4.94 13.13C5.83 13.67 6.88 14 8 14C11.31 14 14 11.31 14 8C14 4.69 11.31 2 8 2Z"
        fill="#25D366"
      />
    </svg>
  )
}
